import type { Ref } from "vue";
import { onMounted, watch } from "vue";

import { cardActions } from "@/action/cardActions";
import { useDelayedAction } from "@/composables/useDelayedAction";
import type { Card } from "@/model/card";
import { useDraggingStore } from "@/store/dragging";
import { useLinkStore } from "@/store/link";
import { eventTarget, isLeftClick, isLinkDrag } from "@/utils/dom/dom";
import { nonMovedPointer } from "@/utils/dom/pointer";

export interface Props {
  el?: Ref<HTMLElement | null>;
  card: Card;
  disabled?: Ref<Readonly<boolean>>;
  delay?: number; // delay in milliseconds
}

export function usePin({ el, card, disabled, delay = 500 }: Props) {
  onMounted(() => {
    if (disabled?.value) return;

    el?.value?.addEventListener("pointerdown", handlePointerDown);
    el?.value?.addEventListener("pointerup", handlePointerUp);
  });

  watch(
    () => useDraggingStore().dragging[card.id],
    () => {
      cancelPin();
      cancelUnpin();
    },
  );

  const handlePointerDown = (event: PointerEvent) => {
    if (disabled?.value || isLinkDrag(event) || !isLeftClick(event)) return;
    if (useLinkStore().isPinned(card.id)) {
      delayedUnpin(event);
    } else {
      delayedPin(event);
    }
  };

  const handlePointerUp = () => {
    cancelPin();
    cancelUnpin();
  };

  // prevent clicks on links when pinning/unpinning
  const linkClickPrevent = (target: HTMLElement) => {
    if (target instanceof HTMLAnchorElement) {
      target.addEventListener("click", (e) => e.preventDefault(), { once: true });
    }
  };

  const [delayedPin, cancelPin] = useDelayedAction((event: PointerEvent) => {
    if (nonMovedPointer(event)) {
      linkClickPrevent(eventTarget(event));
      cardActions.pin("long-press", card.id);
    }
  }, delay);
  const [delayedUnpin, cancelUnpin] = useDelayedAction((event: PointerEvent) => {
    if (nonMovedPointer(event)) {
      linkClickPrevent(eventTarget(event));
      cardActions.unpin("long-press", card.id);
    }
  }, delay);
}
